// Organization Detail Page
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { apiRequest } from '@/lib/supabase';
import { formatRelativeTime } from '@/lib/utils';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
// Imperative toast (module-level, not the hook): avoids mounting the use-toast
// [state] effect in this page so existing unit tests stay stable.
import { toast } from '@/components/shared/ToastProvider';
import { useCurrentUser } from '@/context/PersonaProvider';
import { ArrowLeft, Building2, Key, Users, Edit2, AlertTriangle, UserPlus, Trash2, Check, X, RotateCcw, ShieldAlert, Globe2 } from 'lucide-react';
import { getOrgMetrics, getSaasDetails, tierLabel, type TrustOrgMetricsRow, type SaasLicenseDetailsRow } from '@/lib/api/trust';
import { StatusBadge as TrustStatusBadge } from '@/components/shared/StatusBadge';

// Shape returned by GET /api/v1/organizations/:id — the raw `organizations`
// row (see backend organization.routes.ts). NOTE: this page was originally
// written against a hardcoded ACME mock with fields the real backend never
// returns (plan, status, billing_email, scim_enabled, sso_enabled, mrr, arr)
// plus /licenses and /invoices sub-routes that don't exist. Those have been
// removed. Every optional field below is guarded in render so a missing
// value can never crash the page.
interface OrgDetail {
  id: string;
  name: string;
  domain: string | null;
  address: string | null;
  contact_email: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  stripe_customer_id: string | null;
  parent_id: string | null;
  active: boolean;
  org_kind?: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  // Lifecycle (migration 24): set while the org is scheduled for hard delete.
  deleted_at?: string | null;
  purge_after?: string | null;
  // 18H §4.1 OrganizationDetail delta — ledger + the saas license id (if any).
  ledger?: 'saas' | 'platform' | 'install';
  saas_license_id?: string | null;
}

// Members come from GET /organizations/:id/members, which now presents rows
// through the backend presentUser() (name/email/is_active/mfa_enabled + role).
// Every field is optional so the panel renders defensively regardless of shape.
interface OrgMember {
  id: string;
  name?: string | null;
  full_name?: string | null;
  email?: string | null;
  role: string;
  is_admin?: boolean;
  is_active?: boolean;
  mfa_enabled?: boolean;
  phone?: string | null;
  created_at: string;
}

// Roles selectable in the member controls (super_admin is added only for a
// super_admin caller — mirrors the server-side gate).
const MEMBER_ROLE_OPTIONS: [string, string][] = [
  ['viewer', 'Read Only'],
  ['member', 'Member'],
  ['billing', 'Billing'],
  ['admin', 'Admin'],
];

// Only the fields the backend PATCH endpoint actually accepts.
interface OrgEditForm {
  name?: string;
  domain?: string | null;
  contactEmail?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
}

export function OrganizationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Guard the persona read: this page is always mounted under PersonaProvider in
  // the app, but bare renders (unit tests) have no provider — degrade to "not a
  // super admin" instead of throwing.
  const currentUser = useCurrentUser();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const roleOptions = isSuperAdmin
    ? [...MEMBER_ROLE_OPTIONS, ['super_admin', 'Super Admin'] as [string, string]]
    : MEMBER_ROLE_OPTIONS;

  const [org, setOrg] = useState<OrgDetail | null>(null);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<OrgEditForm>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  // Member management
  const [showAddMember, setShowAddMember] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', name: '', role: 'member' });
  const [addingMember, setAddingMember] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [editMemberRole, setEditMemberRole] = useState<string>('member');
  const [removeMemberTarget, setRemoveMemberTarget] = useState<{ id: string; name: string } | null>(null);
  const [memberError, setMemberError] = useState<string | null>(null);
  // Roster-fetch failure surfaced honestly (banner + retry) — never a silent
  // empty list masquerading as "no members".
  const [membersLoadError, setMembersLoadError] = useState<string | null>(null);

  // Lifecycle / Danger Zone
  const [retentionDays, setRetentionDays] = useState(30);
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [dangerBusy, setDangerBusy] = useState(false);

  // 18H §4.1 — trust-org card (only fetched for ledger='saas').
  const [trustMetrics, setTrustMetrics] = useState<TrustOrgMetricsRow | null>(null);
  const [trustDetails, setTrustDetails] = useState<SaasLicenseDetailsRow | null>(null);
  const [trustLoading, setTrustLoading] = useState(false);
  const [trustLoadError, setTrustLoadError] = useState<string | null>(null);

  const loadTrustCard = async (licenseId: string) => {
    setTrustLoading(true);
    setTrustLoadError(null);
    try {
      const [metrics, details] = await Promise.all([
        getOrgMetrics({ licenseId }),
        getSaasDetails({ licenseId }),
      ]);
      setTrustMetrics(metrics[0] || null);
      setTrustDetails(details[0] || null);
    } catch (err) {
      setTrustLoadError(err instanceof Error ? err.message : 'Failed to load trust org data');
    } finally {
      setTrustLoading(false);
    }
  };

  const reloadMembers = useCallback(async () => {
    try {
      const rows = await apiRequest<OrgMember[]>(`/organizations/${id}/members`);
      setMembers(rows || []);
      setMembersLoadError(null);
    } catch (err) {
      // Honest surfacing — NOT a silent swallow. Show a banner with retry rather
      // than pretending the roster is empty or leaving stale rows unexplained.
      setMembersLoadError(err instanceof Error ? err.message : 'Failed to load members');
    }
  }, [id]);

  const reloadOrg = useCallback(async () => {
    try {
      const updated = await apiRequest<OrgDetail>(`/organizations/${id}`);
      setOrg(updated);
    } catch {
      /* keep current org state on a transient refresh failure */
    }
  }, [id]);

  const addMember = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddingMember(true);
    setMemberError(null);
    try {
      await apiRequest(`/organizations/${id}/members`, {
        method: 'POST',
        body: JSON.stringify(addForm),
      });
      toast({ kind: 'success', title: `Invitation sent to ${addForm.email}` });
      setAddForm({ email: '', name: '', role: 'member' });
      setShowAddMember(false);
      await reloadMembers();
    } catch (err) {
      setMemberError(err instanceof Error ? err.message : 'Failed to add member');
    } finally {
      setAddingMember(false);
    }
  };

  const saveMemberRole = async (userId: string) => {
    try {
      await apiRequest(`/organizations/${id}/members/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role: editMemberRole }),
      });
      setEditingMemberId(null);
      await reloadMembers();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed to update member' });
    }
  };

  const removeMember = async (userId: string) => {
    try {
      await apiRequest(`/organizations/${id}/members/${userId}`, { method: 'DELETE' });
      await reloadMembers();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed to remove member' });
    }
  };

  // ─── Lifecycle actions (Danger Zone) ───────────────────────────────────────
  const deactivateOrg = async () => {
    setDangerBusy(true);
    try {
      await apiRequest(`/organizations/${id}`, { method: 'DELETE' });
      toast({ kind: 'success', title: 'Organization deactivated.' });
      await reloadOrg();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed to deactivate' });
    } finally {
      setDangerBusy(false);
    }
  };

  const scheduleDelete = async () => {
    setDangerBusy(true);
    try {
      await apiRequest(`/organizations/${id}/schedule-delete`, {
        method: 'POST',
        body: JSON.stringify({ retentionDays }),
      });
      toast({ kind: 'success', title: `Deletion scheduled (${retentionDays}d grace).` });
      await reloadOrg();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed to schedule deletion' });
    } finally {
      setDangerBusy(false);
    }
  };

  const restoreOrg = async () => {
    setDangerBusy(true);
    try {
      await apiRequest(`/organizations/${id}/restore`, { method: 'POST' });
      toast({ kind: 'success', title: 'Organization restored.' });
      await reloadOrg();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed to restore' });
    } finally {
      setDangerBusy(false);
    }
  };

  const purgeOrg = async () => {
    if (!org || purgeConfirm !== org.name || dangerBusy) return;
    setDangerBusy(true);
    try {
      await apiRequest(`/organizations/${id}/purge`, {
        method: 'DELETE',
        body: JSON.stringify({ confirm: purgeConfirm }),
      });
      toast({ kind: 'success', title: 'Organization purged.' });
      navigate('/organizations');
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed to purge' });
    } finally {
      setDangerBusy(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      // Decouple the fetches: the org record is the page's source of truth,
      // the members list is a secondary resource. allSettled ensures a
      // failing/missing sub-resource never nukes the main record (or worse,
      // triggers a mock fallback). No mock fallback exists anymore — on a real
      // failure we render an honest error/empty state.
      const [orgResult, memResult] = await Promise.allSettled([
        apiRequest<OrgDetail>(`/organizations/${id}`),
        apiRequest<OrgMember[]>(`/organizations/${id}/members`),
      ]);

      if (orgResult.status === 'fulfilled') {
        setOrg(orgResult.value);
        setEditForm({
          name: orgResult.value.name,
          domain: orgResult.value.domain,
          contactEmail: orgResult.value.contact_email,
          contactName: orgResult.value.contact_name,
          contactPhone: orgResult.value.contact_phone,
        });
        setLoadError(null);
        if (orgResult.value.ledger === 'saas' && orgResult.value.saas_license_id) {
          loadTrustCard(orgResult.value.saas_license_id);
        }
      } else {
        setOrg(null);
        setLoadError(orgResult.reason?.message || 'Failed to load organization');
      }

      if (memResult.status === 'fulfilled') {
        setMembers(memResult.value || []);
        setMembersLoadError(null);
      } else {
        // The org record still renders; the roster shows an honest error + retry
        // instead of a silent empty list (a failing sub-resource never nukes the
        // page, but it also never lies about being empty).
        setMembers([]);
        setMembersLoadError(memResult.reason?.message || 'Failed to load members');
      }
      setLoading(false);
    };
    load();
  }, [id]);

  const saveEdit = async () => {
    setSaveError(null);
    try {
      // Reload from the server — no optimistic/mock state changes.
      const updated = await apiRequest<OrgDetail>(`/organizations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(editForm),
      });
      setOrg(updated);
      setEditing(false);
    } catch (err) {
      // Honest failure: surface the error, leave server-truth state intact.
      setSaveError(err instanceof Error ? err.message : 'Failed to save changes');
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
      <div style={{ textAlign: 'center' }}>
        <div className="animate-spin" style={{ width: '32px', height: '32px', border: '2px solid var(--accent)', borderTopColor: 'transparent', borderRadius: '50%', margin: '0 auto 12px' }} />
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Loading organization...</p>
      </div>
    </div>
  );

  if (!org) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <Link to="/organizations" style={{ color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }}>
            <ArrowLeft size={18} />
          </Link>
          <h1 className="page-title">Organization</h1>
        </div>
        <div style={{ padding: '24px', background: 'var(--ps-pill-red-bg)', border: '1px solid var(--ps-pill-red-bg)', borderRadius: '8px', color: 'var(--ps-pill-red-tx)', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <AlertTriangle size={18} />
          <div>
            <div style={{ fontWeight: 600, fontSize: '14px' }}>Unable to load this organization.</div>
            <div style={{ fontSize: '13px', marginTop: '4px', color: 'var(--ps-pill-red-tx)' }}>{loadError || 'Organization not found.'}</div>
          </div>
        </div>
      </div>
    );
  }

  const status = org.active ? 'active' : 'inactive';
  const kindLabel = (org.org_kind || 'account').toString();

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Link to="/organizations" style={{ color: 'var(--text-secondary)', display: 'flex' }}>
            <ArrowLeft size={18} />
          </Link>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Building2 size={20} style={{ color: 'var(--accent)' }} />
          </div>
          <div>
            <h1 className="page-title">{org.name}</h1>
            <div style={{ display: 'flex', gap: '8px', marginTop: '4px', alignItems: 'center' }}>
              <StatusBadge status={status} />
              <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                {org.domain || 'no domain'} · {kindLabel}
              </span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => { setEditing(!editing); setSaveError(null); }} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Edit2 size={14} /> {editing ? 'Cancel' : 'Edit'}
          </button>
          <Link to={`/licenses/new?org=${id}`}>
            <button style={{ background: 'var(--accent)', color: 'white', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Key size={14} /> Issue License
            </button>
          </Link>
        </div>
      </div>

      <Breadcrumb crumbs={[{ label: 'Accounts', to: '/organizations' }, { label: org.name }]} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
        {[
          { label: 'Members', value: membersLoadError ? '—' : members.length, icon: <Users size={16} /> },
          { label: 'Status', value: org.active ? 'Active' : 'Inactive', icon: <Building2 size={16} /> },
          { label: 'Created', value: new Date(org.created_at).toLocaleDateString(), icon: <Building2 size={16} /> },
        ].map(({ label, value, icon }) => (
          <div key={label} className="card" style={{ padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{label}</span>
              <span style={{ color: 'var(--accent)' }}>{icon}</span>
            </div>
            <div style={{ fontSize: '22px', fontWeight: 700 }}>{value}</div>
          </div>
        ))}
      </div>

      {saveError && (
        <div style={{ padding: '10px 12px', background: 'var(--ps-pill-red-bg)', borderRadius: '6px', marginBottom: '16px', fontSize: '13px', color: 'var(--ps-pill-red-tx)', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertTriangle size={14} /> {saveError}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {/* Org Details */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Organization Details</span>
            {editing && <button onClick={saveEdit} style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '6px', background: 'var(--accent)', color: 'white', border: 'none', cursor: 'pointer' }}>Save</button>}
          </div>
          <div className="card-body">
            {editing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {([
                  { label: 'Name', key: 'name', type: 'text' },
                  { label: 'Domain', key: 'domain', type: 'text' },
                  { label: 'Contact Email', key: 'contactEmail', type: 'email' },
                  { label: 'Contact Name', key: 'contactName', type: 'text' },
                  { label: 'Contact Phone', key: 'contactPhone', type: 'text' },
                ] as { label: string; key: keyof OrgEditForm; type: string }[]).map(({ label, key, type }) => (
                  <div key={key}>
                    <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>{label}</label>
                    <input
                      type={type}
                      value={String((editForm as Record<string, unknown>)[key] ?? '')}
                      onChange={e => setEditForm(prev => ({ ...prev, [key]: e.target.value }))}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <table style={{ width: '100%' }}>
                <tbody>
                  {([
                    ['Name', org.name],
                    ['Domain', org.domain || '—'],
                    ['Kind', <span style={{ textTransform: 'capitalize' }}>{kindLabel}</span>],
                    ['Status', <StatusBadge status={status} />],
                    ['Contact Name', org.contact_name || '—'],
                    ['Contact Email', org.contact_email || '—'],
                    ['Contact Phone', org.contact_phone || '—'],
                    ['Address', org.address || '—'],
                    org.stripe_customer_id ? ['Stripe ID', org.stripe_customer_id] : null,
                    org.parent_id ? ['Parent Org', <Link to={`/organizations/${org.parent_id}`} style={{ color: 'var(--accent)', textDecoration: 'none' }}>{org.parent_id}</Link>] : null,
                    ['Created', formatRelativeTime(org.created_at)],
                    ['Updated', formatRelativeTime(org.updated_at)],
                  ].filter(Boolean) as [string, React.ReactNode][]).map(([k, v]) => (
                    <tr key={String(k)}>
                      <td style={{ padding: '7px 0', fontSize: '12px', color: 'var(--text-muted)', width: '40%', verticalAlign: 'top' }}>{k}</td>
                      <td style={{ padding: '7px 0', fontSize: '13px', fontWeight: 500 }}>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Members */}
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="card-title">Members ({membersLoadError ? '—' : members.length})</span>
            <button onClick={() => { setShowAddMember(v => !v); setMemberError(null); }} style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <UserPlus size={13} /> {showAddMember ? 'Cancel' : 'Add member'}
            </button>
          </div>

          {showAddMember && (
            <form onSubmit={addMember} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '8px', background: 'var(--bg-secondary)' }}>
              <input type="email" required value={addForm.email} onChange={e => setAddForm(p => ({ ...p, email: e.target.value }))} placeholder="member@company.com" style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '13px', outline: 'none' }} />
              <input type="text" required value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))} placeholder="Full name" style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '13px', outline: 'none' }} />
              <div style={{ display: 'flex', gap: '8px' }}>
                <select value={addForm.role} onChange={e => setAddForm(p => ({ ...p, role: e.target.value }))} style={{ flex: 1, padding: '7px 10px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '13px', background: 'var(--surface)' }}>
                  {roleOptions.map(([r, label]) => <option key={r} value={r}>{label}</option>)}
                </select>
                <button type="submit" disabled={addingMember} style={{ padding: '7px 14px', borderRadius: '6px', background: 'var(--accent)', color: 'white', border: 'none', cursor: addingMember ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600 }}>
                  {addingMember ? 'Sending...' : 'Send invite'}
                </button>
              </div>
              {memberError && <div style={{ fontSize: '12px', color: '#DC2626' }}>{memberError}</div>}
            </form>
          )}

          <div style={{ padding: '0 0 8px' }}>
            {membersLoadError ? (
              <div style={{ margin: '12px 16px', padding: '10px 12px', background: 'var(--ps-pill-red-bg)', border: '1px solid var(--ps-pill-red-bg)', borderRadius: '6px', fontSize: '13px', color: 'var(--ps-pill-red-tx)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertTriangle size={14} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1 }}>Couldn't load members: {membersLoadError}</span>
                <button onClick={() => reloadMembers()} style={{ padding: '3px 10px', borderRadius: '6px', border: 'none', background: '#DC2626', color: 'white', fontSize: '12px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>Retry</button>
              </div>
            ) : members.length === 0 ? (
              <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>No members</div>
            ) : members.map(m => {
              const displayName = m.name || m.full_name || m.id;
              const isAdminMember = m.is_admin || m.role === 'admin' || m.role === 'super_admin';
              const canManage = m.role !== 'super_admin' || isSuperAdmin;
              const isEditingRow = editingMemberId === m.id;
              return (
                // ps-mask: rrweb maskTextClass (A20). A customer org's member
                // roster — real people's names, work emails and phone numbers,
                // rendered on a platform-staff screen. Whoever files a bug from
                // this page is our own operator, so they cannot consent on
                // these people's behalf at all. The row's controls, role and
                // MFA state stay legible, which is what a bug about member
                // management is actually about.
                <div key={m.id} className="ps-mask" style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px', gap: '8px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 500 }}>{displayName}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {isAdminMember && <span style={{ fontSize: '11px', color: 'var(--ps-pill-blue-tx)', background: 'var(--ps-pill-blue-bg)', border: '1px solid var(--ps-pill-blue-bg)', padding: '1px 8px', borderRadius: '9999px', fontWeight: 600 }}>Admin</span>}
                      {canManage && !isEditingRow && (
                        <>
                          <button title="Change member role" onClick={() => { setEditingMemberId(m.id); setEditMemberRole(m.role); }} style={{ padding: '3px', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', display: 'flex' }}>
                            <Edit2 size={12} />
                          </button>
                          <button title="Remove member" onClick={() => setRemoveMemberTarget({ id: m.id, name: displayName })} style={{ padding: '3px', borderRadius: '5px', border: '1px solid var(--ps-pill-red-bg)', background: 'var(--ps-pill-red-bg)', color: 'var(--ps-pill-red-tx)', cursor: 'pointer', display: 'flex' }}>
                            <Trash2 size={12} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {isEditingRow ? (
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '6px' }}>
                      <select value={editMemberRole} onChange={e => setEditMemberRole(e.target.value)} style={{ flex: 1, padding: '5px 8px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '12px', background: 'var(--surface)' }}>
                        {roleOptions.map(([r, label]) => <option key={r} value={r}>{label}</option>)}
                      </select>
                      <button title="Save role" onClick={() => saveMemberRole(m.id)} style={{ padding: '4px 6px', borderRadius: '5px', border: 'none', background: 'var(--accent)', color: 'white', cursor: 'pointer', display: 'flex' }}>
                        <Check size={13} />
                      </button>
                      <button title="Cancel" onClick={() => setEditingMemberId(null)} style={{ padding: '4px 6px', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', display: 'flex' }}>
                        <X size={13} />
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                      <span style={{ textTransform: 'capitalize' }}>{m.role}</span>
                      {m.email && <span>{m.email}</span>}
                      {m.phone && <span>{m.phone}</span>}
                      {m.mfa_enabled !== undefined && <span>{m.mfa_enabled ? 'MFA on' : 'MFA off'}</span>}
                      {m.is_active === false && <span style={{ color: '#DC2626' }}>Inactive</span>}
                      <span>Joined {new Date(m.created_at).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 18H §4.1 — trust-org card: profile status/staleness, verified domains,
          at-risk banner. Storage-vs-quota and per-domain proof level aren't
          tracked on the license server today, so they're honestly omitted
          rather than faked. */}
      {org.ledger === 'saas' && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-header">
            <span className="card-title">Trust org</span>
            {trustDetails && <TrustStatusBadge status="saas" />}
          </div>
          <div className="card-body">
            {trustLoading ? (
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Loading…</div>
            ) : trustLoadError ? (
              <div style={{ fontSize: '13px', color: '#DC2626', display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertTriangle size={14} /> {trustLoadError}
              </div>
            ) : !trustMetrics && !trustDetails ? (
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>No trust telemetry has landed for this org yet.</div>
            ) : (
              <>
                {trustMetrics?.metrics.single_admin && (
                  <div style={{ padding: '8px 10px', background: 'var(--ps-pill-amber-bg)', borderRadius: 6, marginBottom: 12, fontSize: '12.5px', color: 'var(--ps-pill-amber-tx)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ShieldAlert size={13} /> Single-admin at-risk — no secondary owner on this profile.
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                  {trustDetails && <><span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Tier</span><span style={{ fontSize: 13, fontWeight: 500 }}>{tierLabel(trustDetails.tier)}</span></>}
                  {trustMetrics && (
                    <>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Published</span>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{trustMetrics.metrics.published ? 'Yes' : 'No (draft)'}</span>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Answers stale</span>
                      <span style={{ fontSize: 13, fontWeight: 500, color: trustMetrics.metrics.answers_stale > 0 ? '#D97706' : 'inherit' }}>
                        {trustMetrics.metrics.answers_stale}/{trustMetrics.metrics.answers_total}
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}><Globe2 size={11} style={{ verticalAlign: -1, marginRight: 3 }} />Verified domains</span>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>
                        {trustMetrics.metrics.verified_domains}
                        {trustMetrics.metrics.domains_in_grace > 0 && <span style={{ color: '#D97706' }}> ({trustMetrics.metrics.domains_in_grace} in grace)</span>}
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Open disputes</span>
                      <span style={{ fontSize: 13, fontWeight: 500, color: trustMetrics.metrics.open_disputes > 0 ? '#DC2626' : 'inherit' }}>{trustMetrics.metrics.open_disputes}</span>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Last ingest</span>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{formatRelativeTime(trustMetrics.ingested_at)}</span>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Danger Zone — org lifecycle (deactivate → schedule-delete → restore → purge) */}
      <div className="card" style={{ marginTop: '16px', borderLeft: '3px solid var(--status-expired, #DC2626)' }}>
        <div className="card-header"><span className="card-title" style={{ color: '#DC2626' }}>Danger Zone</span></div>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {org.deleted_at && (
            <div style={{ padding: '10px 12px', background: 'var(--ps-pill-amber-bg)', border: '1px solid var(--ps-pill-amber-bg)', borderRadius: '6px', fontSize: '13px', color: 'var(--ps-pill-amber-tx)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertTriangle size={15} />
              <span>
                Scheduled for deletion{org.purge_after ? ` — purges after ${new Date(org.purge_after).toLocaleDateString()}` : ''}.
                {' '}Deleted at {new Date(org.deleted_at).toLocaleString()}.
              </span>
            </div>
          )}
          {!org.active && !org.deleted_at && (
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>This organization is deactivated.</div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
            {org.active && (
              <button disabled={dangerBusy} onClick={deactivateOrg} style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: dangerBusy ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600 }}>
                Deactivate
              </button>
            )}
            {(!org.active || org.deleted_at) && (
              <button disabled={dangerBusy} onClick={restoreOrg} style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid var(--ps-pill-green-bg)', background: 'var(--ps-pill-green-bg)', color: 'var(--ps-pill-green-tx)', cursor: dangerBusy ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <RotateCcw size={14} /> Restore
              </button>
            )}
            {!org.deleted_at && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Grace</label>
                <input type="number" min={0} max={3650} value={retentionDays} onChange={e => setRetentionDays(Math.max(0, parseInt(e.target.value || '0', 10)))} style={{ width: '64px', padding: '7px 8px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '13px' }} />
                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>days</span>
                <button disabled={dangerBusy} onClick={scheduleDelete} style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid var(--ps-pill-amber-bg)', background: 'var(--ps-pill-amber-bg)', color: 'var(--ps-pill-amber-tx)', cursor: dangerBusy ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600 }}>
                  Schedule delete
                </button>
              </div>
            )}
          </div>

          {/* Purge is irreversible and the server accepts exactly one thing: this organization's name
              (400 CONFIRM_MISMATCH otherwise — no skeleton-key literal), and only once the deletion is
              scheduled (409 PURGE_NOT_SCHEDULED). The box offers what the server will take (DF-36). */}
          <div style={{ borderTop: '1px dashed var(--border)', paddingTop: '12px' }}>
            {org.deleted_at ? (
              <>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                  Irreversible. Type the organization name (<strong>{org.name}</strong>) exactly to confirm, then purge.
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input value={purgeConfirm} onChange={e => setPurgeConfirm(e.target.value)} placeholder={org.name} aria-label="Type the organization name to confirm the purge" autoComplete="off" style={{ flex: 1, maxWidth: '320px', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '13px' }} />
                  <button
                    disabled={dangerBusy || purgeConfirm !== org.name}
                    onClick={purgeOrg}
                    style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: purgeConfirm === org.name ? 'var(--status-expired, #DC2626)' : '#FCA5A5', color: 'white', cursor: (dangerBusy || purgeConfirm !== org.name) ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 700 }}
                  >
                    Purge
                  </button>
                </div>
              </>
            ) : (
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                Schedule the deletion first. Purge (irreversible) is offered once the organization is in its grace window.
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={removeMemberTarget !== null}
        title="Remove member?"
        body={removeMemberTarget ? `Remove ${removeMemberTarget.name} from this organization? Their account is deactivated and detached from the org.` : ''}
        confirmLabel="Remove"
        danger
        onConfirm={() => {
          if (removeMemberTarget) removeMember(removeMemberTarget.id);
          setRemoveMemberTarget(null);
        }}
        onCancel={() => setRemoveMemberTarget(null)}
      />

      {/* Metadata */}
      {org.metadata && Object.keys(org.metadata).length > 0 && (
        <div className="card" style={{ marginTop: '16px' }}>
          <div className="card-header"><span className="card-title">Metadata</span></div>
          <div className="card-body">
            <pre style={{ fontSize: '12px', fontFamily: 'monospace', background: 'var(--bg-secondary)', padding: '12px', borderRadius: '6px', overflowX: 'auto', margin: 0 }}>
              {JSON.stringify(org.metadata, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
